import { AsyncPipe } from "@angular/common";
import {
    Component,
    inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { BusySpinnerComponent } from "@components/busy/busy-spinner/busy-spinner.component";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { InfoComponent } from "@components/info/info.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { BehaviorSubject, Subject } from "rxjs";
import { catchError, takeUntil } from "rxjs/operators";
import {
    Anskaffningsformer,
    Arbetsordertyper,
    Dokumentstatus,
    DokumentTyp,
    Planeringsstatus,
    Produktionsstatus,
    RaskRoller
} from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { ArbetsorderDisabledFlag } from "~app/features/planera/model/ArbetsorderDisabledFlag";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { VirkeskopKontraktstyp } from "~app/features/virkeskop/models/kontrakt-models";
import { ArbetsorderMediatorService } from "../../services/arbetsorder-mediator.service";
import { ArbetsorderMonitor, ArbetsorderMonitorService } from "../../services/arbetsorder-monitor.service";
import { ArbetsorderDokumentService, IArbetsorderDokument } from "./arbetsorder-dokument.service";
import { ArbetsorderDokumentTexts } from "./arbetsorder-dokument.texts";

@Component({
    selector: "rask-arbetsorder-dokument",
    templateUrl: "./arbetsorder-dokument.component.html",
    styleUrls: ["./arbetsorder-dokument.component.scss"],
    providers: [ArbetsorderDokumentService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ListComponent,
        ListColumnDirective,
        InfoComponent,
        ButtonComponent,
        BusySpinnerComponent,
        CheckboxComponent,
        FormsModule,
        AsyncPipe
    ]
})
export class ArbetsorderDokumentComponent implements OnInit, OnChanges, OnDestroy {
    @Input() arbetsorder: IArbetsorderDetail;

    protected apteringspaketWorking$: BehaviorSubject<boolean>;
    protected avverkningsdirektivWorking$: BehaviorSubject<boolean>;
    protected documents: IArbetsorderDokument[];
    protected hasInactiveDocuments = false;
    protected isLeveransvirke: boolean;
    protected recreateSkordare = true;
    protected recreateSkotare = true;
    protected texts = ArbetsorderDokumentTexts.texts;

    private readonly arbetsorderMediatorService = inject(ArbetsorderMediatorService);
    private readonly arbetsorderMonitor: ArbetsorderMonitor;
    private readonly authenticationService = inject(AuthenticationService);
    private readonly canEdit: boolean;
    private readonly canCreateUtkast: boolean;
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly service = inject(ArbetsorderDokumentService);
    private readonly zone = inject(NgZone);

    constructor() {
        const arbetsorderMonitorService = inject(ArbetsorderMonitorService);

        this.arbetsorderMonitor = arbetsorderMonitorService.connect();
        this.apteringspaketWorking$ = this.arbetsorderMonitor.getWork("apteringsfiler", () => this.fetchUtskrifter());
        this.avverkningsdirektivWorking$ = this.arbetsorderMonitor.getWork("avverkningsdirektiv.user", () => this.fetchUtskrifter());
        this.canEdit = this.authenticationService.hasRoles(RaskRoller.role.Produktionsleda, false);
        this.canCreateUtkast = this.authenticationService.hasRoles([
            RaskRoller.role.Produktionsleda,
            RaskRoller.role.Kopplanera,
            RaskRoller.role.Kopleda,
            RaskRoller.role.Avverkningsplanera,
            RaskRoller.role.Virkeskopa
        ]);
        this.arbetsorderMediatorService.documentUpdated$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(x => {
                this.fetchUtskrifter();
            });
    }

    get canCreateApteringspaket(): boolean {
        return (
            (this.recreateSkordare || this.recreateSkotare) &&
            this.canEdit &&
            !this.apteringspaketWorking$.value &&
            this.arbetsorder.produktsatt &&
            this.arbetsorder.handelssortiment?.some(hs => hs.produkter?.some(pr => pr.produktinstruktioner?.some(pin => pin))) &&
            this.arbetsorder.isEditableForMode(ArbetsorderDisabledFlag.makulerad) &&
            this.arbetsorder.produktionsstatus !== Produktionsstatus.ProduktionGodkand
        );
    }

    get canCreateAvverkningsdirektiv(): boolean {
        return (
            this.canEdit &&
            !this.avverkningsdirektivWorking$.value &&
            !this.arbetsorder.isTerminalImport &&
            this.produktionsStatusToCreateAvverkningsdirektiv() &&
            this.atgarderTillgangliga() &&
            this.arbetsorderNotProduktiongodkand() &&
            this.arbetsorder.isEditableForMode(ArbetsorderDisabledFlag.makulerad)
        );
    }

    get canCreateAvverkningsdirektivUtkast(): boolean {

        const hasOtherThanUtkast = this.documents.some(doc =>
            doc.dokumentstatus !== Dokumentstatus.Utkast && doc.typ === DokumentTyp.Avverkningsdirektiv
        );

        return (
            this.canCreateUtkast &&
            !this.avverkningsdirektivWorking$.value &&
            !this.arbetsorder.isTerminalImport &&
            this.arbetsorder.produktionsstatus === Produktionsstatus.ProduktionEjPaborjad &&
            !hasOtherThanUtkast && // Inget gällande direktiv existerar
            this.arbetsorder.isEditableForMode(ArbetsorderDisabledFlag.makulerad)
        );
    }

    get canRecreateSkordare(): boolean {
        return this.arbetsorder.virkeskopskontraktDetail?.kontraktTyp !== VirkeskopKontraktstyp.Grot && !this.arbetsorder.isGrotskotning;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.arbetsorder && changes.arbetsorder?.previousValue?.id !== this.arbetsorder.id) {
            this.fetchUtskrifter();
        }
    }

    ngOnDestroy(): void {
        this.arbetsorderMonitor.disconnect();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.isLeveransvirke = Anskaffningsformer.isVirkesKop(this.arbetsorder.anskaffningsform)
            && this.arbetsorder.typ === Arbetsordertyper.IngenProduktion;
        this.recreateSkordare = this.canRecreateSkordare;

        this.service.documents$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((x) => {
                this.documents = x;
                this.checkInactiveDocuments(x);
                this.triggerDigestCycle();
            });
        this.fetchUtskrifter();
    }

    protected createApteringspaket(): void {
        this.apteringspaketWorking$.next(true);
        this.service.createApteringspaket(this.arbetsorder.id, this.recreateSkordare, this.recreateSkotare)
            .pipe(catchError(error => {
                this.apteringspaketWorking$.next(false);
                return error;
            }))
            .subscribe();
    }

    protected createAvverkningsdirektiv(): void {
        this.avverkningsdirektivWorking$.next(true);
        this.service.createAvverkningsdirektiv(this.arbetsorder.id)
            .pipe(catchError(error => {
                this.avverkningsdirektivWorking$.next(false);
                return error;
            }))
            .subscribe();
    }

    protected createAvverkningsdirektivUtkast(): void {
        this.avverkningsdirektivWorking$.next(true);
        this.service.createAvverkningsdirektivUtkast(this.arbetsorder.id)
            .pipe(catchError(error => {
                this.avverkningsdirektivWorking$.next(false);
                return error;
            }))
            .subscribe();
    }

    private arbetsorderNotProduktiongodkand(): boolean {
        return this.arbetsorder.produktionsstatus !== Produktionsstatus.ProduktionGodkand;
    }

    private atgarderTillgangliga(): boolean {
        return this.arbetsorder.atgarder.every((x) => x.planeringsstatus === Planeringsstatus.Tillgangligt);
    }

    private checkInactiveDocuments(docs: IArbetsorderDokument[]) {
        this.hasInactiveDocuments = docs.some(d => d.inaktiverad);
    }

    private fetchUtskrifter(): void {
        if (this.arbetsorder) {
            this.service.fetchUtskrifter(this.arbetsorder.id);
        }
    }

    private produktionsStatusToCreateAvverkningsdirektiv(): boolean {
        return this.arbetsorder.produktionsstatus === Produktionsstatus.ProduktionEjPaborjad
            || this.arbetsorder.produktionsstatus === Produktionsstatus.ProduktionPagar;
    }

    private triggerDigestCycle(): void {
        this.zone.run(() => {
            // Medvetet tomt - meddela angularjs
        });
    }
}
